import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faPaperclip,
  faPaperPlane,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../api/api";
import dayjs from "dayjs";
import { MessageText } from "./FeedMessage";
import { deleteIcon, editIcon } from "../../header/svg";
import { news, attach, sendmessage, micicon } from "./chatsvgs";

const urlRegex = /(https?:\/\/[^\s]+)/g;

const NewsFeed = ({ setchat }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null); // new
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const practiseid = window.getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);
  const MAX_ROWS = 4;
  const lineHeightRef = useRef(0);

  const autoResizeTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    // Compute line-height once
    if (!lineHeightRef.current) {
      const cs = window.getComputedStyle(ta);
      lineHeightRef.current = parseFloat(cs.lineHeight || "24"); // fallback ~24px
    }

    // Reset then set to content height, but clamp to MAX_ROWS
    ta.style.height = "auto";
    const maxHeight = lineHeightRef.current * MAX_ROWS; // only content height; paddings are already in scrollHeight
    const newHeight = Math.min(ta.scrollHeight, maxHeight);
    ta.style.height = `${newHeight}px`;

    // Toggle scrolling past 4 rows
    ta.style.overflowY = ta.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [inputText]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    autoResizeTextarea();
  };

  const fetchData = async () => {
    try {
      const response = await api.get(`/v1/${practiseid}/newsfeed`);
      const fetchedMessages = response.data.map((item) => ({
        id: item.id,
        text: item.description || "",
        documents: item.file || "",
        createdOn: item.createdon,
        created_by: item.created_by || "",
        type: "received",
      }));
      fetchedMessages.sort(
        (a, b) => new Date(a.createdOn) - new Date(b.createdOn)
      );
      setMessages(fetchedMessages);
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [practiseid]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!inputText && attachments.length === 0) return;

    // Check if we are editing
    if (editingMessageId) {
      // Call edit API
      try {
        await api.put(`/v1/${practiseid}/newsfeed/update`, null, {
          params: {
            rowid: editingMessageId,
            title: "",
            description: inputText,
            status: "A",
            updated_by: userConfig?.username || "Unknown",
          },
        });

        setMessages((prev) =>
          prev.map((m) =>
            m.id === editingMessageId ? { ...m, text: inputText } : m
          )
        );

        setInputText("");
        setAttachments([]);
        setEditingMessageId(null);
        fileInputRef.current.value = null;
      } catch (error) {
        console.error("Edit Error:", error);
        alert("Failed to update message.");
      }
      return;
    }

    // Normal send
    const formData = new FormData();
    formData.append("description", inputText || "");
    formData.append("created_by", userConfig?.username || "Unknown");
    formData.append("status", "A");
    formData.append("title", "");
    if (attachments.length > 0) {
      formData.append("file", attachments[0]);
    }

    try {
      const response = await api.post(`/v1/${practiseid}/newsfeed`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newMessage = {
        id: response.data.id || Date.now(),
        text: inputText,
        documents:
          attachments.length > 0 ? URL.createObjectURL(attachments[0]) : "",
        type: "sent",
        createdOn: new Date().toISOString(),
        created_by: userConfig?.username || "You",
      };

      setMessages((prev) => [...prev, newMessage]);
      setInputText("");
      setAttachments([]);
      fileInputRef.current.value = null;
    } catch (error) {
      console.error("Send Error:", error);
      alert("Failed to send message.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAttachments([file]);
    e.target.value = null;
  };

  const removeAttachment = () => setAttachments([]);

  // Edit message
  const handleEdit = (id) => {
    const msgToEdit = messages.find((m) => m.id === id);
    if (!msgToEdit) return;
    setInputText(msgToEdit.text);
    setAttachments([]);
    setEditingMessageId(id); // mark as editing
  };

  // Delete message
  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/${practiseid}/newsfeed/delete`, {
        params: { rowid: id, deleted_by: userConfig?.username || "Unknown" },
      });
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (error) {
      console.error("Delete Error:", error);
      alert("Failed to delete message.");
    }
  };

  // const renderMessageText = (text) => {
  //   const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  //   return text.split("\n").map((line, i) => {
  //     const parts = [];
  //     let lastIndex = 0;
  //     let match;

  //     // Use exec to safely find multiple links per line
  //     while ((match = urlRegex.exec(line)) !== null) {
  //       const url = match[0];
  //       const start = match.index;

  //       // Add plain text before the URL
  //       if (start > lastIndex) {
  //         parts.push(line.slice(lastIndex, start));
  //       }

  //       // Normalize link (add https:// if missing)
  //       const href = url.startsWith("http") ? url : `https://${url}`;

  //       // Add clickable link
  //       parts.push(
  //         <a
  //           key={`${i}-${start}`}
  //           href={href}
  //           target="_blank"
  //           rel="noopener noreferrer"
  //           className="text-blue-600 underline break-all"
  //         >
  //           {url}
  //         </a>
  //       );

  //       lastIndex = urlRegex.lastIndex;
  //     }

  //     // Add remaining text after last URL
  //     if (lastIndex < line.length) {
  //       parts.push(line.slice(lastIndex));
  //     }

  //     // Preserve line breaks
  //     return (
  //       <React.Fragment key={i}>
  //         {parts}
  //         {i < text.split("\n").length - 1 && <br />}
  //       </React.Fragment>
  //     );
  //   });
  // };

  return (
    <div className="flex flex-col h-full w-full relative bg-[#FAFBFC]">

      {/* Header */}
      <div className="flex items-center justify-between h-[62px] px-4 bg-white border-b border-[#E5E7EB] flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-[#166FF6] flex items-center justify-center">
            {news}
          </div>
          <span className="font-inter text-[14px] font-semibold text-[#1D2951]">News Feed</span>
        </div>
        {/* <button
          className="w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-[#1D2951] hover:bg-[#F3F4F6] transition-all"
          onClick={() => setchat(false)}
        >
          <FontAwesomeIcon icon={faXmark} className="text-[14px]" />
        </button> */}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" style={{ scrollbarWidth: "none" }}>
        {messages.map((msg) => (
          <div key={msg.id} className="max-w-[600px] mx-auto relative">
            {/* Date Divider */}
            <div className="flex items-center justify-center mb-3">
              <span className="text-[11px] font-medium text-[#9CA3AF] bg-[#F3F4F6] px-3 py-1 rounded-full">
                {dayjs(msg.createdOn).format("DD MMM YYYY, hh:mm A")}
              </span>
            </div>
            {/* Message Card */}
            <div className="bg-white border border-[#E5E7EB] text-[#1C1D22] rounded-xl px-4 pt-4 pb-2 break-words shadow-sm hover:shadow-md transition-shadow">
              {msg.text && <MessageText text={msg.text} />}
              {msg.documents && (
                <div className="mt-3">
                  {msg.documents.endsWith(".pdf") ||
                  msg.documents.endsWith(".docx") ? (
                    <a
                      href={msg.documents}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#166FF6] hover:underline break-all text-[13px]"
                    >
                      {msg.documents.split("/").pop()}
                    </a>
                  ) : (
                    <img
                      src={msg.documents}
                      alt="attachment"
                      className="max-w-[200px] rounded-lg"
                    />
                  )}
                </div>
              )}
              {/* Actions */}
              <div className="flex justify-end items-center gap-1 mt-3 pt-2 border-t border-[#F3F4F6]">
                <button
                  onClick={() => handleEdit(msg.id)}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-[#1D2951] hover:bg-[#F3F4F6] transition-all"
                >
                  {editIcon}
                </button>
                <button
                  onClick={() => handleDelete(msg.id)}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 transition-all"
                >
                  {deleteIcon}
                </button>
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 px-4 py-3 bg-white border-t border-[#E5E7EB]">
        {attachments.length > 0 && (
          <div className="flex gap-2 items-center mb-2">
            <div className="relative">
              <img
                src={URL.createObjectURL(attachments[0])}
                alt={attachments[0].name}
                className="w-16 h-16 rounded-lg object-cover border border-[#E5E7EB]"
              />
              <button
                onClick={removeAttachment}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px]"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>
        )}

        <div className="flex items-end gap-2 max-w-[600px] mx-auto">
          <div className="flex-1 flex items-center bg-[#F7F9FB] border border-[#E5E7EB] rounded-xl px-3 gap-2 focus-within:border-[#1D2951] transition-colors">
            <textarea
              ref={textareaRef}
              rows={1}
              className="bg-transparent flex-grow py-2.5 text-[13px] outline-none resize-none text-[#1D2951] placeholder:text-[#B0B7C3]"
              placeholder="Type a message..."
              value={inputText}
              onChange={handleInputChange}
              onFocus={() => autoResizeTextarea()}
              style={{
                maxHeight: lineHeightRef.current
                  ? `${lineHeightRef.current * MAX_ROWS}px`
                  : undefined,
                overflowY: "hidden",
              }}
            />

            <div className="flex items-center gap-1 pb-2">
              <input
                type="file"
                accept=".pdf,.png,.jpeg,.jpg,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.wav,.mp4,.avi,.mov,.zip,.rar"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                id="fileInput"
              />
              <label htmlFor="fileInput" className="cursor-pointer w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-[#1D2951] hover:bg-[#F3F4F6] transition-all">
                {attach}
              </label>
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-[#1D2951] hover:bg-[#F3F4F6] transition-all cursor-pointer"
                onClick={() => setInputText('')}
              >
                {micicon}
              </div>
            </div>
          </div>

          <button
            onClick={handleSend}
            disabled={!inputText && attachments.length === 0}
            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all mb-[1px] ${
              !inputText && attachments.length === 0
                ? "bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed"
                : "bg-[#1D2951] text-white hover:bg-[#162040] cursor-pointer shadow-sm"
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsFeed;
